import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { statsApi } from '../lib/api';
import MaraSportsLogo from '../components/MaraSportsLogo';
import { 
    Trophy, Users, Calendar, Target, Zap, Globe, 
    ChevronRight, Play, Shield, BarChart3, Search,
    Star, Award, TrendingUp, MapPin, Menu, X, Loader2
} from 'lucide-react';

const LandingPage = () => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(null);
    const [tabData, setTabData] = useState(null);
    const [tabLoading, setTabLoading] = useState(false);

    useEffect(() => {
        const loadStats = async () => {
            try {
                const response = await statsApi.getOverview();
                setStats(response.data);
            } catch (error) {
                console.error('Failed to load stats:', error);
            }
        };
        loadStats();
    }, []);

    const loadTabData = async (tab) => {
        if (activeTab === tab) {
            setActiveTab(null);
            setTabData(null);
            return;
        }
        
        setActiveTab(tab);
        setTabLoading(true);
        try {
            let response;
            switch (tab) {
                case 'athletes':
                    response = await statsApi.getAthletes();
                    break;
                case 'tournaments':
                    response = await statsApi.getTournaments();
                    break;
                case 'verified':
                    response = await statsApi.getVerified();
                    break;
                case 'active':
                    response = await statsApi.getActive();
                    break;
                default:
                    return;
            }
            setTabData(response.data);
        } catch (error) {
            console.error('Failed to load tab data:', error);
        } finally {
            setTabLoading(false);
        }
    };

    const handleSportClick = (sportName) => {
        const urlFriendlyName = sportName.toLowerCase().replace(/ /g, '-');
        navigate(`/sports/${urlFriendlyName}`);
    };

    const sports = [
        { name: 'Athletics Track and Field', icon: '🏃', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Athletics Track and Field')?.athletes || 0, color: '#ffd700' },
        { name: 'Basketball', icon: '🏀', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Basketball')?.athletes || 0, color: '#FF9F0A' },
        { name: 'Hockey', icon: '🏑', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Hockey')?.athletes || 0, color: '#00d4ff' },
        { name: 'Football', icon: '⚽', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Football')?.athletes || 0, color: '#34C759' },
        { name: 'Volleyball', icon: '🏐', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Volleyball')?.athletes || 0, color: '#FF6B35' },
        { name: 'Rugby', icon: '🏉', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Rugby')?.athletes || 0, color: '#8B4513' },
        { name: 'Netball', icon: '🤾', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Netball')?.athletes || 0, color: '#FF69B4' },
        { name: 'Chess', icon: '♟️', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Chess')?.athletes || 0, color: '#1a1a2e' },
        { name: 'Table Tennis', icon: '🏓', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Table Tennis')?.athletes || 0, color: '#E63946' },
        { name: 'Karate', icon: '🥋', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Karate')?.athletes || 0, color: '#2D3436' },
        { name: 'Taekwondo', icon: '🥋', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Taekwondo')?.athletes || 0, color: '#0077B6' },
        { name: 'Badminton', icon: '🏸', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Badminton')?.athletes || 0, color: '#48CAE4' },
        { name: 'Swimming', icon: '🏊', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Swimming')?.athletes || 0, color: '#007AFF' },
        { name: 'Tennis', icon: '🎾', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Tennis')?.athletes || 0, color: '#AAFF00' },
        { name: 'Handball', icon: '🤾', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Handball')?.athletes || 0, color: '#9B59B6' },
        { name: 'Judo', icon: '🥋', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Judo')?.athletes || 0, color: '#E74C3C' },
        { name: 'Padel', icon: '🎾', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Padel')?.athletes || 0, color: '#AF52DE' },
        { name: 'Squash', icon: '🎾', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Squash')?.athletes || 0, color: '#FF6347' },
        { name: 'E-Sports', icon: '🎮', athletes: stats?.sports_breakdown?.find(s => s.sport === 'E-Sports')?.athletes || 0, color: '#9146FF' },
        { name: 'Motor Racing', icon: '🏎️', athletes: stats?.sports_breakdown?.find(s => s.sport === 'Motor Racing')?.athletes || 0, color: '#DC143C' },
    ];

    const features = [
        {
            icon: <Shield className="w-6 h-6" />,
            title: 'Athlete Digital Profile',
            description: 'Verified sports identity with performance stats, rankings, and achievements.'
        },
        {
            icon: <Calendar className="w-6 h-6" />,
            title: 'Competition Infrastructure',
            description: 'Create and manage tournaments with AI-powered scheduling.'
        },
        {
            icon: <Search className="w-6 h-6" />,
            title: 'Talent Discovery Engine',
            description: 'Advanced search for scouts to find the next champions.'
        },
        {
            icon: <Zap className="w-6 h-6" />,
            title: 'AI Match Predictions',
            description: 'Data-driven insights and match outcome predictions.'
        },
        {
            icon: <BarChart3 className="w-6 h-6" />,
            title: 'School Rankings',
            description: 'ESPN-style rankings for African schools and academies.'
        },
        {
            icon: <Globe className="w-6 h-6" />,
            title: 'Pan-African Network',
            description: 'Connect athletes, coaches, and scouts across the continent.'
        }
    ];

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Grid overlay */}
            <div className="fixed inset-0 grid-lines pointer-events-none z-0" />
            
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-3">
                            <MaraSportsLogo className="h-12" />
                        </Link>
                        
                        {/* Desktop Nav */}
                        <div className="hidden md:flex items-center gap-8">
                            <Link to="/tournaments" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="nav-tournaments">
                                Tournaments
                            </Link>
                            <Link to="/schools/leaderboard" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="nav-schools">
                                School Rankings
                            </Link>
                            <Link to="/talent" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="nav-talent">
                                Talent Discovery
                            </Link>
                            <Link to="/ai-predictions" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="nav-ai">
                                AI Predictions
                            </Link>
                            <Link to="/rankings" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="nav-rankings">
                                Rankings
                            </Link>
                            <Link to="/sponsorship" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="nav-sponsorship">
                                Sponsors
                            </Link>
                        </div>
                        
                        <div className="hidden md:flex items-center gap-4">
                            {isAuthenticated ? (
                                <Button 
                                    onClick={() => navigate('/dashboard')} 
                                    className="bg-primary hover:bg-red-600 text-white border-none btn-glow uppercase tracking-widest text-xs font-bold h-10 px-6"
                                    data-testid="nav-dashboard-btn"
                                >
                                    Dashboard
                                </Button>
                            ) : (
                                <>
                                    <Button 
                                        variant="ghost" 
                                        onClick={() => navigate('/auth')}
                                        className="text-sm text-muted-foreground hover:text-white"
                                        data-testid="nav-login-btn"
                                    >
                                        Sign In
                                    </Button>
                                    <Button 
                                        onClick={() => navigate('/auth?mode=register')} 
                                        className="bg-primary hover:bg-red-600 text-white border-none btn-glow uppercase tracking-widest text-xs font-bold h-10 px-6"
                                        data-testid="nav-register-btn"
                                    >
                                        Get Started
                                    </Button>
                                </>
                            )}
                        </div>
                        
                        {/* Mobile menu button */}
                        <button 
                            className="md:hidden p-2"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-btn"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
                
                {/* Mobile menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden bg-[#0d2847] border-t border-white/5 py-4 px-6">
                        <div className="flex flex-col gap-4">
                            <Link to="/tournaments" className="text-sm text-muted-foreground hover:text-white">Tournaments</Link>
                            <Link to="/schools/leaderboard" className="text-sm text-muted-foreground hover:text-white">School Rankings</Link>
                            <Link to="/talent" className="text-sm text-muted-foreground hover:text-white">Talent Discovery</Link>
                            <Link to="/ai-predictions" className="text-sm text-muted-foreground hover:text-white">AI Predictions</Link>
                            <Link to="/rankings" className="text-sm text-muted-foreground hover:text-white">Rankings</Link>
                            <Link to="/sponsorship" className="text-sm text-muted-foreground hover:text-white">Sponsors</Link>
                            <hr className="border-white/10" />
                            {isAuthenticated ? (
                                <Button onClick={() => navigate('/dashboard')} className="bg-primary text-white w-full">Dashboard</Button>
                            ) : (
                                <>
                                    <Button variant="outline" onClick={() => navigate('/auth')} className="w-full border-white/20">Sign In</Button>
                                    <Button onClick={() => navigate('/auth?mode=register')} className="bg-primary text-white w-full">Get Started</Button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center pt-16">
                <div className="hero-glow absolute inset-0 z-0" />
                
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                    <img 
                        src="https://images.pexels.com/photos/12585946/pexels-photo-12585946.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                        alt="Athlete in starting blocks"
                        className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] via-transparent to-[#0a1628]" />
                </div>
                
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7 space-y-8">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10">
                                <span className="w-2 h-2 bg-primary animate-pulse" />
                                <span className="text-xs tracking-widest uppercase text-muted-foreground">
                                    Launching at NextGen Tournaments Festival 2026
                                </span>
                            </div>
                            
                            <h1 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter uppercase leading-none animate-slide-up">
                                The Digital<br />
                                <span className="gold-text">Operating System</span><br />
                                For Sports
                            </h1>
                            
                            <p className="text-lg md:text-xl text-muted-foreground max-w-xl animate-slide-up delay-200">
                                Build your athlete identity. Compete in tournaments. Get discovered by scouts. 
                                The future of African sports starts here.
                            </p>
                            
                            <div className="flex flex-wrap gap-4 animate-slide-up delay-300">
                                <Button 
                                    size="lg" 
                                    onClick={() => navigate('/auth?mode=register')}
                                    className="bg-primary hover:bg-cyan-400 text-[#0a1628] border-none btn-glow uppercase tracking-widest text-sm font-bold h-12 px-8"
                                    data-testid="hero-cta-btn"
                                >
                                    Register Now
                                    <ChevronRight className="w-4 h-4 ml-2" />
                                </Button>
                                <Button 
                                    size="lg" 
                                    variant="outline"
                                    onClick={() => navigate('/tournaments')}
                                    className="border-white/20 hover:border-white hover:bg-white/5 uppercase tracking-widest text-sm font-bold h-12 px-8"
                                    data-testid="hero-explore-btn"
                                >
                                    <Play className="w-4 h-4 mr-2" />
                                    Explore Tournaments
                                </Button>
                            </div>
                        </div>
                        
                        {/* Stats Cards - Clickable Tabs */}
                        <div className="lg:col-span-5 grid grid-cols-2 gap-4">
                            <div 
                                className={`bg-[#0d2847] border p-6 animate-slide-up delay-100 cursor-pointer transition-all hover:border-primary/50 ${activeTab === 'athletes' ? 'border-primary ring-1 ring-primary/30' : 'border-white/5'}`}
                                onClick={() => loadTabData('athletes')}
                                data-testid="stat-tab-athletes"
                            >
                                <Users className="w-8 h-8 text-primary mb-4" />
                                <p className="stat-display text-4xl md:text-5xl text-white">{stats?.total_athletes || 0}</p>
                                <p className="text-xs tracking-widest uppercase text-muted-foreground mt-2">Athletes</p>
                            </div>
                            <div 
                                className={`bg-[#0d2847] border p-6 animate-slide-up delay-200 cursor-pointer transition-all hover:border-accent/50 ${activeTab === 'tournaments' ? 'border-accent ring-1 ring-accent/30' : 'border-white/5'}`}
                                onClick={() => loadTabData('tournaments')}
                                data-testid="stat-tab-tournaments"
                            >
                                <Trophy className="w-8 h-8 text-accent mb-4" />
                                <p className="stat-display text-4xl md:text-5xl text-white">{stats?.total_tournaments || 0}</p>
                                <p className="text-xs tracking-widest uppercase text-muted-foreground mt-2">Tournaments</p>
                            </div>
                            <div 
                                className={`bg-[#0d2847] border p-6 animate-slide-up delay-300 cursor-pointer transition-all hover:border-success/50 ${activeTab === 'verified' ? 'border-success ring-1 ring-success/30' : 'border-white/5'}`}
                                onClick={() => loadTabData('verified')}
                                data-testid="stat-tab-verified"
                            >
                                <Shield className="w-8 h-8 text-success mb-4" />
                                <p className="stat-display text-4xl md:text-5xl text-white">{stats?.verified_athletes || 0}</p>
                                <p className="text-xs tracking-widest uppercase text-muted-foreground mt-2">Verified</p>
                            </div>
                            <div 
                                className={`bg-[#0d2847] border p-6 animate-slide-up delay-400 cursor-pointer transition-all hover:border-secondary/50 ${activeTab === 'active' ? 'border-secondary ring-1 ring-secondary/30' : 'border-white/5'}`}
                                onClick={() => loadTabData('active')}
                                data-testid="stat-tab-active"
                            >
                                <Zap className="w-8 h-8 text-secondary mb-4" />
                                <p className="stat-display text-4xl md:text-5xl text-white">{stats?.active_tournaments || 0}</p>
                                <p className="text-xs tracking-widest uppercase text-muted-foreground mt-2">Active Now</p>
                            </div>
                        </div>
                    </div>

                    {/* Expandable Tab Content */}
                    {activeTab && (
                        <div className="mt-8 animate-slide-up" data-testid="tab-content-panel">
                            <div className="bg-[#0d2847] border border-white/10 p-6 md:p-8">
                                {tabLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                    </div>
                                ) : tabData ? (
                                    <div>
                                        {/* Athletes Tab Content */}
                                        {activeTab === 'athletes' && (
                                            <div>
                                                <div className="flex items-center justify-between mb-6">
                                                    <h3 className="font-heading font-bold text-xl uppercase tracking-tight">
                                                        Athletes Overview
                                                    </h3>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => navigate('/talent')}
                                                        className="border-white/20 text-xs"
                                                    >
                                                        View All <ChevronRight className="w-3 h-3 ml-1" />
                                                    </Button>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-primary">{tabData.total}</p>
                                                        <p className="text-xs text-muted-foreground">Total</p>
                                                    </div>
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-success">{tabData.verified}</p>
                                                        <p className="text-xs text-muted-foreground">Verified</p>
                                                    </div>
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-accent">{tabData.by_country?.length || 0}</p>
                                                        <p className="text-xs text-muted-foreground">Countries</p>
                                                    </div>
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-secondary">{tabData.by_age_category?.length || 0}</p>
                                                        <p className="text-xs text-muted-foreground">Age Groups</p>
                                                    </div>
                                                </div>
                                                {tabData.by_country && tabData.by_country.length > 0 && (
                                                    <div className="mb-6">
                                                        <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Top Countries</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {tabData.by_country.slice(0, 5).map((c, i) => (
                                                                <span key={i} className="px-3 py-1 bg-primary/10 text-primary text-sm">
                                                                    {c.country}: {c.count}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {tabData.recent && tabData.recent.length > 0 && (
                                                    <div>
                                                        <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Recent Athletes</p>
                                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                                            {tabData.recent.slice(0, 5).map((athlete, i) => (
                                                                <div 
                                                                    key={i} 
                                                                    className="bg-[#0a1628] p-3 border border-white/5 cursor-pointer hover:border-primary/30 transition-colors"
                                                                    onClick={() => navigate(`/athletes/${athlete.id}`)}
                                                                >
                                                                    <p className="font-bold text-sm truncate">{athlete.full_name}</p>
                                                                    <p className="text-xs text-muted-foreground">{athlete.country}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Tournaments Tab Content */}
                                        {activeTab === 'tournaments' && (
                                            <div>
                                                <div className="flex items-center justify-between mb-6">
                                                    <h3 className="font-heading font-bold text-xl uppercase tracking-tight">
                                                        Tournaments Overview
                                                    </h3>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => navigate('/tournaments')}
                                                        className="border-white/20 text-xs"
                                                    >
                                                        View All <ChevronRight className="w-3 h-3 ml-1" />
                                                    </Button>
                                                </div>
                                                <div className="grid grid-cols-3 gap-4 mb-6">
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-accent">{tabData.total}</p>
                                                        <p className="text-xs text-muted-foreground">Total</p>
                                                    </div>
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-success">{tabData.active}</p>
                                                        <p className="text-xs text-muted-foreground">Active</p>
                                                    </div>
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-muted-foreground">{tabData.completed}</p>
                                                        <p className="text-xs text-muted-foreground">Completed</p>
                                                    </div>
                                                </div>
                                                {tabData.by_sport && tabData.by_sport.length > 0 && (
                                                    <div className="mb-6">
                                                        <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">By Sport</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {tabData.by_sport.slice(0, 6).map((s, i) => (
                                                                <span key={i} className="px-3 py-1 bg-accent/10 text-accent text-sm">
                                                                    {s.sport}: {s.count}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {tabData.upcoming && tabData.upcoming.length > 0 && (
                                                    <div>
                                                        <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Upcoming Tournaments</p>
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                            {tabData.upcoming.slice(0, 3).map((t, i) => (
                                                                <div 
                                                                    key={i} 
                                                                    className="bg-[#0a1628] p-4 border border-white/5 cursor-pointer hover:border-accent/30 transition-colors"
                                                                    onClick={() => navigate(`/tournaments/${t.id}`)}
                                                                >
                                                                    <p className="font-bold text-sm truncate">{t.name}</p>
                                                                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                                                        <MapPin className="w-3 h-3" /> {t.location}
                                                                    </p>
                                                                    <p className="text-xs text-accent mt-1">{t.sport}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Verified Tab Content */}
                                        {activeTab === 'verified' && (
                                            <div>
                                                <div className="flex items-center justify-between mb-6">
                                                    <h3 className="font-heading font-bold text-xl uppercase tracking-tight">
                                                        Verified Athletes
                                                    </h3>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => navigate('/talent?verified=true')}
                                                        className="border-white/20 text-xs"
                                                    >
                                                        View All <ChevronRight className="w-3 h-3 ml-1" />
                                                    </Button>
                                                </div>
                                                <div className="grid grid-cols-3 gap-4 mb-6">
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-success">{tabData.total_verified}</p>
                                                        <p className="text-xs text-muted-foreground">Verified</p>
                                                    </div>
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-accent">{tabData.pending}</p>
                                                        <p className="text-xs text-muted-foreground">Pending</p>
                                                    </div>
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-red-500">{tabData.rejected}</p>
                                                        <p className="text-xs text-muted-foreground">Rejected</p>
                                                    </div>
                                                </div>
                                                {tabData.by_sport && tabData.by_sport.length > 0 && (
                                                    <div className="mb-6">
                                                        <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Verified by Sport</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {tabData.by_sport.slice(0, 6).map((s, i) => (
                                                                <span key={i} className="px-3 py-1 bg-success/10 text-success text-sm">
                                                                    {s.sport}: {s.count}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {tabData.recent_verified && tabData.recent_verified.length > 0 && (
                                                    <div>
                                                        <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Recently Verified</p>
                                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                                            {tabData.recent_verified.slice(0, 5).map((athlete, i) => (
                                                                <div 
                                                                    key={i} 
                                                                    className="bg-[#0a1628] p-3 border border-white/5 cursor-pointer hover:border-success/30 transition-colors"
                                                                    onClick={() => navigate(`/athletes/${athlete.id}`)}
                                                                >
                                                                    <div className="flex items-center gap-1 mb-1">
                                                                        <Shield className="w-3 h-3 text-success" />
                                                                        <p className="font-bold text-sm truncate">{athlete.full_name}</p>
                                                                    </div>
                                                                    <p className="text-xs text-muted-foreground">{athlete.country}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Active Tab Content */}
                                        {activeTab === 'active' && (
                                            <div>
                                                <div className="flex items-center justify-between mb-6">
                                                    <h3 className="font-heading font-bold text-xl uppercase tracking-tight">
                                                        Active Tournaments
                                                    </h3>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => navigate('/tournaments?status=active')}
                                                        className="border-white/20 text-xs"
                                                    >
                                                        View All <ChevronRight className="w-3 h-3 ml-1" />
                                                    </Button>
                                                </div>
                                                <div className="grid grid-cols-3 gap-4 mb-6">
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-secondary">{tabData.total_active}</p>
                                                        <p className="text-xs text-muted-foreground">Total Active</p>
                                                    </div>
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-primary">{tabData.registration_open}</p>
                                                        <p className="text-xs text-muted-foreground">Registration Open</p>
                                                    </div>
                                                    <div className="bg-[#0a1628] p-4 border border-white/5">
                                                        <p className="text-2xl font-bold text-success">{tabData.in_progress}</p>
                                                        <p className="text-xs text-muted-foreground">In Progress</p>
                                                    </div>
                                                </div>
                                                {tabData.by_sport && tabData.by_sport.length > 0 && (
                                                    <div className="mb-6">
                                                        <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Active by Sport</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {tabData.by_sport.slice(0, 6).map((s, i) => (
                                                                <span key={i} className="px-3 py-1 bg-secondary/10 text-secondary text-sm">
                                                                    {s.sport}: {s.count}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {tabData.tournaments && tabData.tournaments.length > 0 && (
                                                    <div>
                                                        <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Live Tournaments</p>
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                            {tabData.tournaments.slice(0, 6).map((t, i) => (
                                                                <div 
                                                                    key={i} 
                                                                    className="bg-[#0a1628] p-4 border border-white/5 cursor-pointer hover:border-secondary/30 transition-colors"
                                                                    onClick={() => navigate(`/tournaments/${t.id}`)}
                                                                >
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <span className={`px-2 py-0.5 text-xs ${t.tournament_status === 'in_progress' ? 'bg-success/20 text-success' : 'bg-primary/20 text-primary'}`}>
                                                                            {t.tournament_status === 'in_progress' ? 'Live' : 'Open'}
                                                                        </span>
                                                                    </div>
                                                                    <p className="font-bold text-sm truncate">{t.name}</p>
                                                                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                                                        <MapPin className="w-3 h-3" /> {t.location}
                                                                    </p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Sports Section */}
            <section className="relative py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="text-center mb-16">
                        <p className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Supported Sports</p>
                        <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight uppercase">
                            All Sports. One Platform.
                        </h2>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {sports.map((sport, index) => (
                            <div 
                                key={sport.name}
                                className="bg-[#0d2847] border border-white/5 p-6 text-center card-hover group cursor-pointer hover:border-primary/30 transition-all"
                                style={{ '--sport-accent': sport.color }}
                                onClick={() => handleSportClick(sport.name)}
                                data-testid={`sport-card-${sport.name.toLowerCase().replace(/ /g, '-')}`}
                            >
                                <div className="text-4xl mb-4">{sport.icon}</div>
                                <h3 className="font-heading font-bold text-lg uppercase tracking-tight">{sport.name}</h3>
                                <p className="stat-display text-2xl mt-2" style={{ color: sport.color }}>{sport.athletes}</p>
                                <p className="text-xs text-muted-foreground">Athletes</p>
                                <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span className="text-xs text-primary flex items-center justify-center gap-1">
                                        View Details <ChevronRight className="w-3 h-3" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="relative py-24 md:py-32 bg-[#0d2847]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                        <div className="lg:col-span-4 lg:sticky lg:top-24">
                            <p className="text-xs tracking-widest uppercase text-primary mb-4">Platform Features</p>
                            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase mb-6">
                                Everything You Need To Dominate
                            </h2>
                            <p className="text-muted-foreground mb-8">
                                From verified athlete profiles to AI-powered predictions, 
                                we're building the complete infrastructure for African youth sports.
                            </p>
                            <Button 
                                onClick={() => navigate('/auth?mode=register')}
                                className="bg-primary hover:bg-cyan-400 text-[#0a1628] border-none btn-glow uppercase tracking-widest text-xs font-bold"
                                data-testid="features-cta-btn"
                            >
                                Start Building Your Legacy
                            </Button>
                        </div>
                        
                        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {features.map((feature, index) => (
                                <div 
                                    key={feature.title}
                                    className="bg-[#0a1628] border border-white/5 p-8 card-hover"
                                    data-testid={`feature-card-${index}`}
                                >
                                    <div className="w-12 h-12 bg-white/5 flex items-center justify-center mb-6 text-primary">
                                        {feature.icon}
                                    </div>
                                    <h3 className="font-heading font-bold text-xl uppercase tracking-tight mb-3">
                                        {feature.title}
                                    </h3>
                                    <p className="text-muted-foreground text-sm">
                                        {feature.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Talent Discovery Preview */}
            <section className="relative py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="relative">
                            <img 
                                src="https://images.pexels.com/photos/27294512/pexels-photo-27294512.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="African youth football"
                                className="w-full aspect-[4/3] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
                        </div>
                        
                        <div className="space-y-8">
                            <p className="text-xs tracking-widest uppercase text-accent mb-4">For Scouts & Recruiters</p>
                            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight uppercase">
                                Discover The Next Generation
                            </h2>
                            <p className="text-muted-foreground text-lg">
                                Search through verified athlete profiles. Filter by sport, age, country, 
                                and performance metrics. Find your next star before anyone else.
                            </p>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
                                        <Target className="w-5 h-5 text-primary" />
                                    </div>
                                    <span className="text-sm">Advanced Filters</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-accent/10 flex items-center justify-center">
                                        <Star className="w-5 h-5 text-accent" />
                                    </div>
                                    <span className="text-sm">Verified Profiles</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-success/10 flex items-center justify-center">
                                        <TrendingUp className="w-5 h-5 text-success" />
                                    </div>
                                    <span className="text-sm">Performance Data</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-secondary/10 flex items-center justify-center">
                                        <MapPin className="w-5 h-5 text-secondary" />
                                    </div>
                                    <span className="text-sm">Location Search</span>
                                </div>
                            </div>
                            
                            <Button 
                                onClick={() => navigate('/talent')}
                                className="bg-accent hover:bg-amber-600 text-black border-none uppercase tracking-widest text-xs font-bold h-11 px-8"
                                data-testid="talent-cta-btn"
                            >
                                Explore Talent Pool
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-24 md:py-32 bg-[#0d2847]">
                <div className="hero-glow absolute inset-0 z-0" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 text-center">
                    <h2 className="font-heading font-black text-4xl md:text-6xl lg:text-7xl tracking-tighter uppercase mb-8">
                        Your Legacy<br />
                        <span className="gold-text">Starts Now</span>
                    </h2>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-12">
                        Join thousands of athletes building their digital sports identity. 
                        Create your passport, compete in tournaments, and get discovered.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <Button 
                            size="lg"
                            onClick={() => navigate('/auth?mode=register')}
                            className="bg-primary hover:bg-cyan-400 text-[#0a1628] border-none btn-glow uppercase tracking-widest text-sm font-bold h-14 px-10"
                            data-testid="cta-register-btn"
                        >
                            Register Now
                            <ChevronRight className="w-5 h-5 ml-2" />
                        </Button>
                        <Button 
                            size="lg"
                            variant="outline"
                            onClick={() => navigate('/tournaments')}
                            className="border-white/20 hover:border-white hover:bg-white/5 uppercase tracking-widest text-sm font-bold h-14 px-10"
                            data-testid="cta-tournaments-btn"
                        >
                            Browse Tournaments
                        </Button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-white/5 py-12">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <MaraSportsLogo className="h-10" />
                        <p className="text-xs text-muted-foreground">
                            © 2026 NextGen Tournaments. The Digital Operating System for Youth Sports.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
